import { APP_DATE_FORMAT_MID, AUTHORITIES } from "@/config/constants";
import { useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoCourseQuiz } from "@/shared/model/edo-course-quiz.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
} from "@/components/ui";
import { BarChart3, ClipboardList, Pencil, MoreVertical, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { TextFormat } from "react-jhipster";
import { useNavigate } from "react-router-dom";
import { cn } from "@/shared/util/cn";

interface ICourseQuizRowProps {
  courseQuiz: IEdoCourseQuiz;
  onOpen: (courseQuiz: IEdoCourseQuiz) => void;
  onEdit: (quiz: IEdoCourseQuiz) => void;
  onDelete: (quiz: IEdoCourseQuiz) => void;
}

export const CourseQuizRow = (props: ICourseQuizRowProps) => {
  const navigate = useNavigate();
  const [localQuiz, setLocalQuiz] = useState<IEdoCourseQuiz>(props.courseQuiz);
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const { courseQuiz, onDelete: handleDelete, onEdit: handleEdit, onOpen: handleOpen } = props;

  const handleEditQuestions = () => {
    navigate(`/course-quizzes/${courseQuiz.id}`);
  };

  useEffect(() => {
    if (courseQuiz) {
      setLocalQuiz(courseQuiz);
    }
  }, [courseQuiz]);

  const canManage = hasAnyAuthority(account.roles, [
    AUTHORITIES.ROLE_ADMIN,
    AUTHORITIES.ROLE_TEACHER,
    AUTHORITIES.ROLE_VICE,
  ]);

  return (
    <ListItem
      className={cn(
        "flex cursor-pointer items-center gap-3 py-3 transition-colors hover:bg-primary/5"
      )}
      onClick={() => handleOpen(localQuiz)}
    >
      <ListItemIcon>
        <Avatar
          alt={localQuiz.quiz?.title}
          fallback={localQuiz.quiz?.title?.[0] ?? "Q"}
          className="bg-primary/10 text-primary"
        />
      </ListItemIcon>
      <ListItemText
        primary={localQuiz.quiz?.title}
        secondary={
          <TextFormat
            type="date"
            value={localQuiz.createdAt}
            format={APP_DATE_FORMAT_MID}
            locale="it"
          />
        }
      />
      {canManage && (
        <ListItemSecondaryAction onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton aria-label="Menu quiz" size="sm">
                <MoreVertical className="h-4 w-4" />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleEdit(localQuiz);
                }}
              >
                <Pencil className="h-4 w-4" />
                Modifica quiz
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleEditQuestions();
                }}
              >
                <ClipboardList className="h-4 w-4" />
                Modifica domande
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  navigate(
                    `/courses/${localQuiz.course?.id}/quizzes/${localQuiz.id}/results`
                  );
                }}
              >
                <BarChart3 className="h-4 w-4" />
                Visualizza risultati
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleDelete(localQuiz);
                }}
                className="text-red-600 focus:text-red-600 dark:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
                Elimina quiz
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

export default CourseQuizRow;
